/**
 * Pure helpers for the "Multi AI Upload" timetable flow.
 *
 * The Multi AI Upload uploads ONE whole-day screenshot; the edge function
 * returns artists already grouped by stage. These helpers turn that result
 * into timetable rows and merge them into the existing day's timetable
 * WITHOUT touching stages the AI didn't detect (the producer's choice:
 * "alleen herkende stages vervangen").
 *
 * Kept free of React/Supabase imports so they can be unit-tested in isolation.
 */

/** A row coming back from the extract-lineup edge function. */
export interface ExtractedRow {
  artist_name: string;
  set_start: string;
  set_end: string;
  stage_name: string;
  event_date: string;
}

/** A timetable row as held in the briefing editor state. */
export interface TimetableRow {
  event_date: string;
  stage_name: string;
  stage_id?: string | null;
  artist_name: string;
  set_start: string;
  set_end: string;
  highlighted?: boolean;
}

/**
 * Attach the briefing_stages FK to each extracted row by looking up its
 * (canonical) stage_name. Carrying stage_id keeps the row matchable even if
 * the producer later renames the stage — mirrors how addTimetableItem stamps
 * stage_id in LineupImportTab.
 */
export function resolveExtractedStageIds(
  rows: ExtractedRow[],
  stageIdByName: Map<string, string>,
): TimetableRow[] {
  return rows.map((r) => ({
    event_date: r.event_date,
    stage_name: r.stage_name,
    stage_id: stageIdByName.get(r.stage_name) ?? null,
    artist_name: r.artist_name,
    set_start: r.set_start,
    set_end: r.set_end,
  }));
}

type StageRow = { stage_name: string; stage_id?: string | null; event_date: string };

/** Name-based bucket key (always available). */
function nameKey(row: StageRow): string {
  return `${row.event_date}::name:${row.stage_name}`;
}

/** FK-based bucket key, or null when the row carries no stage_id. */
function idKey(row: StageRow): string | null {
  return row.stage_id ? `${row.event_date}::id:${row.stage_id}` : null;
}

/**
 * Merge whole-day extraction results into the existing timetable, replacing
 * only the (stage, day) buckets that actually appear in `extracted`. Stages
 * the AI didn't detect are left exactly as they were. Returns a new array.
 *
 * Each extracted row contributes BOTH its name key and (when present) its FK
 * key to the detected set, and an existing row is replaced when EITHER of its
 * keys matches. This keeps matching symmetric regardless of which side carries
 * a stage_id — a row added before its stage had an FK (stage_id null) is still
 * matched by name — while the FK key preserves rename survival. Stage labels
 * are unique per day (briefing_stages UNIQUE(briefing_id, label)), so the name
 * key can't collide across distinct stages.
 */
export function mergeDetectedStages(
  existing: TimetableRow[],
  extracted: TimetableRow[],
): TimetableRow[] {
  const detectedKeys = new Set<string>();
  for (const row of extracted) {
    detectedKeys.add(nameKey(row));
    const ik = idKey(row);
    if (ik) detectedKeys.add(ik);
  }
  const untouched = existing.filter((row) => {
    if (detectedKeys.has(nameKey(row))) return false;
    const ik = idKey(row);
    return !(ik && detectedKeys.has(ik));
  });
  return [...untouched, ...extracted];
}
